import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  type ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideAppFooter } from '@sbe-spinoff-client/app-footer';
import { provideAppShell, withNoMatchRedirect } from '@sbe-spinoff-client/app-shell';
import {
  memberPickerApiBaseUrlInterceptor,
  provideMemberPickerApiBaseUrl,
} from '@sbe-spinoff-schoolbell-e/member-picker-config';
import {
  provideSpinoffAuth,
  resolveOrgScopedId,
  spinoffAuthInterceptor,
  socialSdkDefaults,
  type SpinoffAuthMethod,
} from '@sbe-spinoff-client/spinoff-platform';
import { appRoutes, MEMBER_PICKER_PATH, OAUTH_AUTHORIZE_PATH } from './app.routes';
import { orgApiBaseUrlInterceptor } from '../core/org-api-base-url.interceptor';
import { environment } from '../environments/environment';

// schoolbell-e/web은 sms/timetable/commerce(Phase 2, "학교종이로 계속하기" 단일 창구 —
// methods: [], oidcProviders만)와 반대 방향이다 — schoolbell-e 자신이 OP(신원의 뿌리)라
// 위임할 "다른 앱"이 없다. 그래서 methods를 전부 채우고 oidcProviders는 비운다. 단,
// phone+password(schoolbell-e의 실제 로그인 방식) 조합은 SpinoffAuthMethod에 없다
// (email-password는 진짜 email 전용, phone-otp는 비밀번호 없는 OTP 단독) — <spinoff-login>
// 제네릭 컴포넌트를 그대로 쓸 수 없어(login.component.ts 참고, 자체 UI 유지 + 서비스만 교체),
// 이 배열은 SpinoffAuthService 자체의 동작에는 영향이 없지만 SpinoffLoginComponent를 다른
// 화면에서 쓰게 될 경우를 위해 실제 지원 방식만 정직하게 선언해 둔다.
const loginMethods: SpinoffAuthMethod[] = ['social-google', 'social-apple', 'social-kakao'];

export const appConfig: ApplicationConfig = {
  providers: [
    // zoneless 변경 감지 — 컴포넌트는 전부 OnPush이고, 소셜 로그인 SDK 콜백(NgZone.run() 래핑)·레거시 세션 브리지
    // postMessage는 전부 signal.set()으로 귀결된다.
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    provideAnimationsAsync(),
    // orgs/:orgId를 org-layout(사이드 내비게이션) 아래 children(teams/members/labels 등)으로
    // 중첩하면서, 기본 전략('emptyOnly')으로는 path가 있는 child가 상위 :orgId를 상속받지
    // 못한다. 다른 라우트엔 이름이 겹치는 중첩 파라미터가 없어 'always'로 바꿔도 영향 없다.
    provideRouter(
      appRoutes,
      // 공유 단체 설정 화면(spinoff-platform `orgSettingsRoutes`)이 orgId·제목을 input()으로 받는다.
      withComponentInputBinding(),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withNoMatchRedirect(),
    ),
    provideSpinoffAuth({
      apiBaseUrl: environment.apiUrl,
      methods: loginMethods,
      social: {
        google: { clientId: socialSdkDefaults.googleClientId },
        apple: { servicesId: socialSdkDefaults.appleServicesId },
        kakao: { jsAppKey: socialSdkDefaults.kakaoJsAppKey },
      },
      loginPath: '/login',
      // /orgs/:orgId/ 하위 요청에 org access token을 자동 발급·첨부한다(sms/timetable/commerce
      // user-web과 동일 배선, org-scoped-route.util.ts 참고). 백엔드는 이미 준비돼 있다 —
      // SpinoffBffModule의 POST /auth/org-token(spinoff-auth-mount.module.ts)과
      // OrgsModule.onModuleInit의 setOrgRoleResolver(schoolbell-e 자체 Group/GroupMember
      // 기반)가 이미 등록돼 있었다. 가드 쪽(org-access-guard-simplification.md ③)이 아직
      // 이 토큰의 orgId/role claim을 소비하지 않아 당장 매 요청 DB 재조회를 skip하지는
      // 않지만, org-scoped 요청마다 이 claim이 실린 토큰이 오가는 경로 자체를 먼저
      // 깔아두는 선행 작업이다.
      resolveOrgScopedId,
    }),
    // 순서 고정: spinoffAuthInterceptor가 먼저 상대경로(`/oauth/member-picker/...`)를 보고
    // config.apiBaseUrl과 매치 안 돼 그냥 통과시킨 뒤에야 memberPickerApiBaseUrlInterceptor가
    // 절대경로로 바꾼다. 순서를 뒤집으면 이미 절대경로가 된 요청이 apiBaseUrl과 우연히
    // 일치해 무인증이어야 할 member-picker 요청에 Bearer 토큰이 붙는다.
    provideHttpClient(
      withFetch(),
      withInterceptors([orgApiBaseUrlInterceptor, spinoffAuthInterceptor, memberPickerApiBaseUrlInterceptor]),
    ),
    provideMemberPickerApiBaseUrl(environment.apiUrl),
    // 부팅 시 로그인 상태 복원(access token/refresh 쿠키 → 실패 시 레거시 sbe-web-v4 세션
    // 계승)은 더 이상 여기서 부트스트랩을 블로킹하며 돌지 않는다 — `BootGateService`
    // (shared/boot-gate.service.ts)로 옮겨, 콜드스타트로 API 응답이 늦어도(최대 70초)
    // 화면은 즉시 뜨고 가드가 걸린 화면에서만 콘텐츠 영역에 로딩 표시를 한다(app.ts의
    // `showBootLoading`, 2026-09-24). 가드(`authGuard`/`redirectIfLoggedInGuard`)와
    // `AuthorizeComponent`가 그 서비스의 `whenReady()`로 완료를 기다린다.
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { subscriptSizing: 'dynamic', appearance: 'outline' },
    },
    // /apps(App/OAuthClient 등록·관리)는 schoolbell-e-developer-web으로 물리적으로 분리됐다
    // (2026-08-05 developer-web-domain-separation 플랜) — 더 이상 이 앱의 라우트가 아니므로
    // routerLink가 아니라 절대 URL로 링크한다.
    provideAppFooter({
      linkGroups: [
        {
          title: '개발자',
          links: [{ label: '개발자센터', href: `${environment.developerWebOrigin}/apps` }],
        },
      ],
    }),
    provideAppShell({
      showThemeToggle: true,
      // oauth/authorize(풀페이지 리다이렉트)·member-picker(RP window.open() 팝업) 둘 다
      // 다른 origin/제3자 컨텍스트에서 도달할 수 있는 화면이라, 이 앱에 과거 저장된
      // 선택이 아니라 OS 설정만 따르게 한다(theme.service.ts 주석 참고). app.routes.ts의
      // 실제 라우트 path와 같은 상수를 참조해 리네임 시 조용히 어긋나지 않게 합니다.
      themeIgnoresStoredRoutePrefixes: [`/${OAUTH_AUTHORIZE_PATH}`, `/${MEMBER_PICKER_PATH}`],
    }),
  ],
};
